import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

export interface JobBoard {
  id: number;
  name: string;
  organization: number;
  color?: string;
  is_default?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface JobBoardStatus {
  id: number;
  board: number;
  name: string;
  order: number;
}

// Fetch all job boards
export function useJobBoards() {
  return useQuery({
    queryKey: ['job-boards'],
    queryFn: async () => {
      const response = await apiClient.get<JobBoard[] | { results: JobBoard[] }>('/api/boards/');
      return Array.isArray(response.data) ? response.data : (response.data as any).results || [];
    },
  });
}

// Fetch statuses for a specific board
export function useJobBoardStatuses(boardId: number | null) {
  return useQuery({
    queryKey: ['job-board-statuses', boardId],
    queryFn: async () => {
      if (!boardId) return [];
      const response = await apiClient.get<JobBoardStatus[] | { results: JobBoardStatus[] }>(
        `/api/board-statuses/?board=${boardId}`
      );
      const data = Array.isArray(response.data) ? response.data : (response.data as any).results || [];
      // Sort by order
      return data.sort((a: JobBoardStatus, b: JobBoardStatus) => (a.order || 0) - (b.order || 0));
    },
    enabled: !!boardId,
  });
}

// Create a new board
export function useCreateJobBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; color?: string }): Promise<JobBoard> => {
      const response = await apiClient.post<JobBoard>('/api/boards/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-boards'] });
    },
  });
}

// Update a board
export function useUpdateJobBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<JobBoard> }) => {
      const response = await apiClient.patch(`/api/boards/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-boards'] });
    },
  });
}

// Delete a board
export function useDeleteJobBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/boards/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-boards'] });
    },
  });
}

// Create a new status
export function useCreateJobBoardStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { board: number; name: string; order: number }): Promise<JobBoardStatus> => {
      const response = await apiClient.post<JobBoardStatus>('/api/board-statuses/', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['job-board-statuses', variables.board] });
    },
  });
}

// Update a status
export function useUpdateJobBoardStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<JobBoardStatus> }): Promise<JobBoardStatus> => {
      const response = await apiClient.patch<JobBoardStatus>(`/api/board-statuses/${id}/`, data);
      return response.data;
    },
    onSuccess: (result: JobBoardStatus) => {
      queryClient.invalidateQueries({ queryKey: ['job-board-statuses', result.board] });
    },
  });
}

// Delete a status
export function useDeleteJobBoardStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, boardId }: { id: number; boardId: number }) => {
      await apiClient.delete(`/api/board-statuses/${id}/`);
      return boardId;
    },
    onSuccess: (boardId) => {
      queryClient.invalidateQueries({ queryKey: ['job-board-statuses', boardId] });
    },
  });
}

// Batch update status orders
export function useUpdateJobStatusOrders() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ boardId, updates }: { 
      boardId: number; 
      updates: { id: number; order: number }[] 
    }) => {
      await Promise.all(
        updates.map(update =>
          apiClient.patch(`/api/board-statuses/${update.id}/`, { order: update.order })
        )
      );
      return boardId;
    },
    onSuccess: (boardId) => {
      queryClient.invalidateQueries({ queryKey: ['job-board-statuses', boardId] });
    },
  });
}